import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { catchError, debounceTime, filter, map, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { DeepPartial, Theme } from '../../shared-services/theme.types';
import { ThemeService } from '../../shared-services/theme.service';
import { executeNavigationFromConfig } from '../../shared-services/chart-wrapper.service';
import { commaSeparatedValue, oneDecimalWithCommas } from '../../utils/formatting.utils';

type SmartTableColumnType =
  | 'text'
  | 'number'
  | 'currency'
  | 'slider'
  | 'progress'
  | 'status'
  | 'dropdown'
  | 'action';
type SmartTableAlign = 'left' | 'center' | 'right';
type SmartTableScope = 'main' | 'popup';

interface SmartTableOption {
  label: string;
  value: any;
  disabled?: boolean;
}

interface SmartTablePopupAction {
  label: string;
  navigateAction?: Record<string, any>;
  closeOnClick?: boolean;
}

interface SmartTablePopupConfig {
  title?: string;
  apiUrl?: string;
  method?: string;
  rows?: any[];
  rowsPath?: string;
  dataKey?: string;
  responsePath?: string;
  columns?: SmartTableColumn[];
  columnBindings?: Record<string, Partial<SmartTableColumn>>;
  headerValues?: Record<string, string>;
  headerActions?: SmartTablePopupAction[];
  tableHeight?: string | number;
  emptyStateMessage?: string;
  requestPayloadMode?: 'auto' | 'custom' | 'merge' | 'none';
  requestPayload?: any;
  queryParams?: Record<string, any>;
  inheritApaPricingLever?: boolean;
  apaRangesUrl?: string;
  apaRangeLabelKey?: string;
  apaRangeValueKey?: string;
  apaRangeMinKey?: string;
  apaRangeMaxKey?: string;
  apaRangeStepKey?: string;
  apaRangeTargetColumnKey?: string;
  apaRangeProductTypeKey?: string;
}

interface SmartTableRowPopupConfig extends SmartTablePopupConfig {
  enabled?: boolean;
  rowIndexes?: Array<number | 'n' | 'N'>;
  rowIds?: Array<string | number>;
  rowIdKey?: string;
  matchKey?: string;
  matchValue?: any;
  matchValues?: any[];
}

interface SmartTableSubmitActionConfig {
  label?: string;
  apiUrl?: string;
  method?: string;
  requestPayload?: any;
  queryParams?: Record<string, any>;
  autoSubmitOnEdit?: boolean;
  triggerColumns?: string[];
  showButton?: boolean;
  debounceMs?: number;
  syncFiltersOnEdit?: boolean;
  filterListener?: string | string[];
  filterKeys?: string[];
  navigateAction?: Record<string, any>;
  successMessage?: string;
  errorMessage?: string;
  responseMessagePath?: string;
  align?: 'start' | 'center' | 'end';
}

interface SmartTableColumn {
  key: string;
  bindingKey?: string;
  header?: string;
  headerKey?: string;
  type?: SmartTableColumnType;
  editable?: boolean;
  width?: string;
  align?: SmartTableAlign;
  placeholder?: string;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  min?: number;
  max?: number;
  step?: number;
  minKey?: string;
  maxKey?: string;
  stepKey?: string;
  progressValueKey?: string;
  progressMax?: number;
  progressMaxKey?: string;
  progressColor?: string;
  progressColorKey?: string;
  statusColor?: string;
  statusColorKey?: string;
  compactFormat?: boolean;
  compactFormatKey?: string;
  compactUnit?: 'auto' | 'k' | 'm';
  compactUnitKey?: string;
  compactDecimals?: number;
  options?: any[];
  dropdownApiUrl?: string;
  dropdownMethod?: string;
  dropdownResponsePath?: string;
  dropdownLabelKey?: string;
  dropdownValueKey?: string;
  dropdownDisabledKey?: string;
  dropdownPlaceholder?: string;
  dropdownRequestPayload?: any;
  dropdownQueryParams?: Record<string, any>;
  actionLabel?: string;
  actionVariant?: 'button' | 'icon';
  navigateAction?: Record<string, any>;
  popup?: SmartTablePopupConfig;
}

interface SmartTableFormulaConfig {
  confidenceKey?: string;
  priceLiftKey?: string;
  riskKey?: string;
  resourceLoadKey?: string;
  baseValueKey?: string;
}

interface SmartTableRecalculationConfig {
  enabled?: boolean;
  triggerAfterChanges?: number | string | string[] | {
    count?: number;
    columnKeys?: string[];
    keys?: string[];
    columns?: string[];
    bindingKeys?: string[];
  };
  watchColumns?: string[];
  apiUrl?: string;
  method?: string;
  responsePath?: string;
  formula?: SmartTableFormulaConfig;
  inputKeys?: {
    confidence?: string;
    priceLift?: string;
    risk?: string;
    resourceLoad?: string;
    baseRevenue?: string;
  };
  scoreKey?: string;
  scoreColorKey?: string;
  projectionKey?: string;
  statusKey?: string;
  statusColorKey?: string;
  syncKey?: string;
  syncColorKey?: string;
  outputKeys?: {
    score?: string;
    scoreColor?: string;
    projection?: string;
    status?: string;
    statusColor?: string;
    syncState?: string;
    syncColor?: string;
  };
  syncStateColors?: {
    pending?: string;
    success?: string;
    error?: string;
  };
}

interface SmartTableConfig {
  widgetId?: string;
  apiUrl?: string;
  method?: string;
  configUrl?: string;
  mockDataUrl?: string;
  columnBindings?: Record<string, Partial<SmartTableColumn>>;
  rows?: any[];
  rowsPath?: string;
  dataKey?: string;
  columns?: SmartTableColumn[];
  headerValues?: Record<string, string>;
  tableHeight?: string | number;
  emptyStateMessage?: string;
  rowPopup?: SmartTableRowPopupConfig;
  themeOverrides?: DeepPartial<Theme>;
  recalculation?: SmartTableRecalculationConfig;
  submitAction?: SmartTableSubmitActionConfig;
  filterListener?: string | string[];
  filterListner?: string | string[];
  inheritApaPricingLever?: boolean;
  apaRangesUrl?: string;
  apaRangeLabelKey?: string;
  apaRangeValueKey?: string;
  apaRangeMinKey?: string;
  apaRangeMaxKey?: string;
  apaRangeStepKey?: string;
  apaRangeTargetColumnKey?: string;
  apaRangeProductTypeKey?: string;
}

interface RecalculationBand {
  min?: number;
  label?: string;
  color?: string;
}

interface RecalculationRules {
  weights?: Record<string, number>;
  projectionConfidenceBonus?: number;
  projectionRiskPenalty?: number;
  projectionLoadPenalty?: number;
  syncMessage?: string;
  statusBands?: RecalculationBand[];
}

interface SmartTableViewState {
  scope: SmartTableScope;
  title: string;
  columns: SmartTableColumn[];
  rows: any[];
  loading: boolean;
  errorMessage: string;
  tableHeight: string;
  emptyStateMessage: string;
  headerValues?: Record<string, string>;
  headerActions?: SmartTablePopupAction[];
}

interface SmartTableTemplateContext {
  row?: any;
  column?: SmartTableColumn;
  event?: any;
}

@Component({
  selector: 'app-json-driven-smart-table',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './json-driven-smart-table.component.html',
  styleUrl: './json-driven-smart-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JsonDrivenSmartTableComponent implements OnInit, OnDestroy {
  private static readonly DEBUG_KEY = '[PAT-SLIDER-TRACE-20260527]';
  private static readonly APA_OVERRIDES_STORAGE_KEY = 'dvm_apa_range_overrides';
  private readonly destroy$ = new Subject<void>();
  private readonly recalculationTrigger$ = new Subject<void>();
  private initialized = false;
  private inputData: SmartTableConfig | null = null;
  private readonly dropdownOptionsCache = new Map<string, SmartTableOption[]>();
  private readonly dropdownLoadingKeys = new Set<string>();
  private autoSubmitTimer?: ReturnType<typeof setTimeout>;
  private pendingAutoSubmit = false;

  @Input() set data(value: SmartTableConfig | null) {
    this.inputData = value;
    if (this.initialized) {
      this.initializeComponent();
    }
  }

  get data(): SmartTableConfig | null {
    return this.inputData;
  }

  loading = false;
  apiSyncing = false;
  errorMessage = '';
  syncMessage = '';
  tableHeight = '28rem';
  columns: SmartTableColumn[] = [];
  rows: any[] = [];
  widgetConfig: SmartTableConfig | null = null;
  editedCellKeys = new Set<string>();
  lastEditedRow: any = null;

  popupOpen = false;
  popupTable: SmartTableViewState = this.createEmptyTableState('popup');
  popupContextRow: any = null;
  popupContextColumn: SmartTableColumn | undefined;

  submitInFlight = false;
  submitMessage = '';
  submitErrorMessage = '';

  constructor(
    private configService: ConfigService,
    private chartFilterHandler: ChartFilterHandlerService,
    private globalFilterService: GlobalFilterService,
    private responsiveLayoutService: ResponsiveLayoutService,
    private themeService: ThemeService,
    private cdr: ChangeDetectorRef,
    private hostElement: ElementRef<HTMLElement>,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.initialized = true;

    this.themeService.theme$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.applyResolvedTheme();
      this.cdr.markForCheck();
    });

    this.responsiveLayoutService.reflow$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.cdr.markForCheck();
    });

    this.globalFilterService.filter$
      .pipe(takeUntil(this.destroy$))
      .subscribe((filters) => {
        if (!this.widgetConfig?.widgetId || !this.widgetConfig) {
          return;
        }

        const shouldReload = this.chartFilterHandler.shouldReload(filters, this.widgetConfig.widgetId);
        this.debug('filter$ event', {
          widgetId: this.widgetConfig.widgetId,
          shouldReload,
          filters: this.summarizeFilters(filters),
        });

        if (!shouldReload) {
          return;
        }

        this.loadRowsFromConfig(this.widgetConfig, filters);
      });

    this.recalculationTrigger$
      .pipe(
        debounceTime(250),
        filter(() => this.shouldRunRecalculation()),
        switchMap(() => this.runRecalculationRequest()),
        takeUntil(this.destroy$),
      )
      .subscribe((rules) => {
        if (rules) {
          this.applyRecalculationRules(rules);
        }
      });

    this.initializeComponent();
  }

  ngOnDestroy(): void {
    if (this.autoSubmitTimer) {
      clearTimeout(this.autoSubmitTimer);
      this.autoSubmitTimer = undefined;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  get editedCellCount(): number {
    return this.editedCellKeys.size;
  }

  get editTriggerCount(): number {
    const triggerConfig = this.widgetConfig?.recalculation?.triggerAfterChanges;
    if (typeof triggerConfig === 'number') {
      return Math.max(1, Number(triggerConfig || 3));
    }

    const objectCount = Number(
      triggerConfig && typeof triggerConfig === 'object' && !Array.isArray(triggerConfig)
        ? triggerConfig.count
        : undefined,
    );
    if (Number.isFinite(objectCount) && objectCount > 0) {
      return Math.max(1, objectCount);
    }

    const configuredColumns = this.getTriggerColumnKeys();
    if (configuredColumns.length > 0) {
      return configuredColumns.length;
    }

    return 3;
  }

  get emptyStateMessage(): string {
    return this.widgetConfig?.emptyStateMessage || 'No table rows available.';
  }

  get hasSubmitAction(): boolean {
    return !!this.widgetConfig?.submitAction && this.widgetConfig?.submitAction?.showButton !== false;
  }

  get submitButtonLabel(): string {
    return this.widgetConfig?.submitAction?.label || 'Submit';
  }

  get submitButtonAlignClass(): string {
    const align = this.widgetConfig?.submitAction?.align || 'end';
    return `align-${align}`;
  }

  getRowClasses(scope: SmartTableScope, row: any, rowIndex: number): Record<string, boolean> {
    return {
      'clickable-row': this.hasRowPopup(scope, row, rowIndex),
    };
  }

  trackByColumn = (_: number, column: SmartTableColumn): string => column.bindingKey || column.key;
  trackByRow = (_: number, row: any): string => this.getRowId(row, _);

  initializeComponent(): void {
    if (!this.initialized || !this.inputData) {
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.syncMessage = '';
    this.submitMessage = '';
    this.submitErrorMessage = '';
    this.submitInFlight = false;
    this.columns = [];
    this.rows = [];
    this.popupOpen = false;
    this.popupTable = this.createEmptyTableState('popup');
    this.dropdownOptionsCache.clear();
    this.dropdownLoadingKeys.clear();
    if (this.autoSubmitTimer) {
      clearTimeout(this.autoSubmitTimer);
      this.autoSubmitTimer = undefined;
    }
    this.pendingAutoSubmit = false;
    this.editedCellKeys.clear();
    this.lastEditedRow = null;
    this.cdr.markForCheck();

    const hasInlineConfig = Array.isArray(this.inputData.columns) || Array.isArray(this.inputData.rows);
    const configUrl = this.inputData.configUrl || (hasInlineConfig ? '' : this.inputData.apiUrl || '');

    if (!configUrl) {
      const mergedConfig = this.mergeWidgetConfigs(null, this.inputData);
      this.applyWidgetConfig(mergedConfig);
      return;
    }

    this.configService
      .getConfig(configUrl, this.inputData, undefined, this.resolveMethod(configUrl))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (remoteConfig) => this.applyWidgetConfig(this.mergeWidgetConfigs(remoteConfig, this.inputData!)),
        error: () => {
          this.loading = false;
          this.errorMessage = 'Unable to load table configuration.';
          this.cdr.markForCheck();
        },
      });
  }

  applyWidgetConfig(config: SmartTableConfig): void {
    this.widgetConfig = config;
    this.columns = this.normalizeColumns(config);
    this.tableHeight = this.normalizeCssSize(config.tableHeight || '28rem');
    this.applyResolvedTheme();

    if (this.columns.length === 0) {
      this.loading = false;
      this.errorMessage = 'No table columns were configured. Define columns in dashboard JSON or table config.';
      this.cdr.markForCheck();
      return;
    }

    this.loadRowsFromConfig(config, this.getInitialFiltersForWidget(config));
  }

  getHeaderLabel(column: SmartTableColumn, scope: SmartTableScope = 'main'): string {
    const headerKey = column.headerKey || '';
    const headerValues = scope === 'popup' ? this.popupTable.headerValues : this.widgetConfig?.headerValues;
    const configuredHeader = headerKey ? headerValues?.[headerKey] : '';
    return configuredHeader || column.header || this.humanize(column.key);
  }

  isSliderColumn(column: SmartTableColumn): boolean {
    return column.type === 'slider';
  }

  isProgressColumn(column: SmartTableColumn): boolean {
    return column.type === 'progress';
  }

  isStatusColumn(column: SmartTableColumn): boolean {
    return column.type === 'status';
  }

  isDropdownColumn(column: SmartTableColumn): boolean {
    return column.type === 'dropdown';
  }

  isActionColumn(column: SmartTableColumn): boolean {
    return column.type === 'action';
  }

  hasPopup(column: SmartTableColumn): boolean {
    return !!column.popup;
  }

  isEditableColumn(column: SmartTableColumn): boolean {
    return column.editable === true;
  }

  isPopupTriggerColumn(column: SmartTableColumn): boolean {
    return this.hasPopup(column);
  }

  getCellAlignment(column: SmartTableColumn): SmartTableAlign {
    return column.align || (this.isNumberLikeColumn(column) ? 'right' : 'left');
  }

  getCellClasses(column: SmartTableColumn): Record<string, boolean> {
    const alignment = this.getCellAlignment(column);
    return {
      'align-left': alignment === 'left',
      'align-center': alignment === 'center',
      'align-right': alignment === 'right',
      'editable-cell': this.isEditableColumn(column),
      'slider-column': this.isSliderColumn(column),
      'progress-column': this.isProgressColumn(column),
      'status-column': this.isStatusColumn(column),
      'dropdown-column': this.isDropdownColumn(column),
      'action-column': this.isActionColumn(column),
      'popup-trigger-column': this.isPopupTriggerColumn(column),
    };
  }

  getColumnWidth(column: SmartTableColumn): string | null {
    return column.width || null;
  }

  getTableMinWidth(columns: SmartTableColumn[]): string | null {
    if (!Array.isArray(columns) || columns.length === 0) {
      return null;
    }

    const totalWidth = columns.reduce((sum, column) => sum + this.resolveColumnPixelWidth(column.width), 0);
    if (totalWidth <= 0) {
      return null;
    }

    return `${Math.max(totalWidth + 100, 640)}px`;
  }

  getDisplayValue(row: any, column: SmartTableColumn): string {
    const value = this.getValueByPath(row, column.key);
    if (value === undefined || value === null || value === '') {
      return '-';
    }

    if (column.type === 'currency') {
      return this.formatCurrencyValue(value, column, row);
    }

    if (column.type === 'number' || this.isNumberLikeColumn(column)) {
      return this.formatNumericValue(value, column, row);
    }

    return `${column.prefix || ''}${String(value)}${column.suffix || ''}`;
  }

  getInputValue(row: any, column: SmartTableColumn): string | number {
    const value = this.getValueByPath(row, column.key);
    if (value === undefined || value === null) {
      return '';
    }
    return value as string | number;
  }

  onInputChange(scope: SmartTableScope, rowIndex: number, row: any, column: SmartTableColumn, event: Event): void {
    const input = event.target as HTMLInputElement;
    const nextValue = this.isNumberLikeColumn(column)
      ? this.normalizeNumericColumnValue(this.parseNumber(input.value), column)
      : input.value;
    row[column.key] = nextValue;
  }

  onInputCommit(scope: SmartTableScope, rowIndex: number, row: any, column: SmartTableColumn): void {
    this.registerEdit(scope, rowIndex, row, column);
  }

  onSliderChange(scope: SmartTableScope, rowIndex: number, row: any, column: SmartTableColumn, event: Event): void {
    const input = event.target as HTMLInputElement;
    row[column.key] = this.normalizeNumericColumnValue(this.parseNumber(input.value), column);
  }

  onSliderCommit(scope: SmartTableScope, rowIndex: number, row: any, column: SmartTableColumn): void {
    this.registerEdit(scope, rowIndex, row, column);
  }

  onDropdownFocus(rowIndex: number, row: any, column: SmartTableColumn): void {
    this.ensureDropdownOptions(rowIndex, row, column);
  }

  onDropdownChange(scope: SmartTableScope, rowIndex: number, row: any, column: SmartTableColumn, event: Event): void {
    const select = event.target as HTMLSelectElement;
    row[column.key] = this.resolveDropdownValue(select.value, rowIndex, row, column);
    this.registerEdit(scope, rowIndex, row, column);
  }

  getDropdownOptions(rowIndex: number, row: any, column: SmartTableColumn): SmartTableOption[] {
    const staticOptions = this.extractStaticOptions(column);
    if (staticOptions.length > 0) {
      return staticOptions;
    }

    const cacheKey = this.buildDropdownCacheKey(rowIndex, row, column);
    return this.dropdownOptionsCache.get(cacheKey) || [];
  }

  isDropdownLoading(rowIndex: number, row: any, column: SmartTableColumn): boolean {
    const cacheKey = this.buildDropdownCacheKey(rowIndex, row, column);
    return this.dropdownLoadingKeys.has(cacheKey);
  }

  getDropdownValue(row: any, column: SmartTableColumn): string {
    const value = this.getValueByPath(row, column.key);
    return value === undefined || value === null ? '' : String(value);
  }

  getActionLabel(row: any, column: SmartTableColumn): string {
    const configured = this.resolveDynamicValue(column.actionLabel, { row, column });
    if (configured !== undefined && configured !== null && configured !== '') {
      return String(configured);
    }

    if (this.isActionColumn(column)) {
      return 'Open';
    }

    return this.getDisplayValue(row, column);
  }

  onActionClick(row: any, column: SmartTableColumn, event?: Event): void {
    event?.stopPropagation();

    if (column.popup) {
      this.openPopup(row, column, event);
      return;
    }

    if (column.navigateAction) {
      executeNavigationFromConfig(
        this.router,
        { navigateAction: column.navigateAction, widgetId: this.widgetConfig?.widgetId },
        { name: column.key, value: this.getActionLabel(row, column) },
        { row, column },
      );
    }
  }

  hasRowPopup(scope: SmartTableScope, row: any, rowIndex: number): boolean {
    return !!this.resolveRowPopupConfig(scope, row, rowIndex);
  }

  onRowClick(scope: SmartTableScope, row: any, rowIndex: number, event: Event): void {
    const popupConfig = this.resolveRowPopupConfig(scope, row, rowIndex);
    if (!popupConfig || this.isInteractiveTarget(event.target)) {
      return;
    }

    this.openPopupFromConfig(row, undefined, popupConfig, event);
  }

  openPopup(row: any, column: SmartTableColumn, event?: Event): void {
    const popupConfig = column.popup;
    if (!popupConfig) {
      return;
    }

    this.openPopupFromConfig(row, column, popupConfig, event);
  }

  onPopupActionClick(action: SmartTablePopupAction): void {
    if (!action?.navigateAction) {
      return;
    }

    executeNavigationFromConfig(
      this.router,
      { navigateAction: action.navigateAction, widgetId: this.widgetConfig?.widgetId },
      { name: 'popup-action', value: action.label },
      { row: this.popupContextRow, column: this.popupContextColumn },
    );

    if (action.closeOnClick !== false) {
      this.closePopup();
    }
  }

  private openPopupFromConfig(
    row: any,
    column: SmartTableColumn | undefined,
    popupConfig: SmartTablePopupConfig,
    event?: Event,
  ): void {
    event?.stopPropagation();

    this.popupContextRow = row;
    this.popupContextColumn = column;

    const popupColumns = this.normalizeColumns({
      columns: popupConfig.columns || [],
      columnBindings: popupConfig.columnBindings || {},
    });

    const headerActions = this.resolvePopupHeaderActions(popupConfig, row, column);

    this.popupOpen = true;
    this.popupTable = {
      scope: 'popup',
      title: String(this.resolveDynamicValue(popupConfig.title || this.getHeaderLabel(column || { key: 'details' } as SmartTableColumn), { row, column }) || ''),
      columns: popupColumns,
      rows: [],
      loading: true,
      errorMessage: '',
      tableHeight: this.normalizeCssSize(popupConfig.tableHeight || '22rem'),
      emptyStateMessage: popupConfig.emptyStateMessage || 'No popup rows available.',
      headerValues: popupConfig.headerValues,
      headerActions,
    };
    this.cdr.markForCheck();

    const inlineRows = this.extractRowsFromSource(popupConfig.rows, popupConfig);
    if (inlineRows.length > 0) {
      this.resolvePopupRowsForDisplay(inlineRows, popupConfig)
        .pipe(takeUntil(this.destroy$))
        .subscribe((rows) => {
          this.popupTable = {
            ...this.popupTable,
            rows,
            loading: false,
          };
          this.cdr.markForCheck();
        });
      return;
    }

    const rawApiUrl = popupConfig.apiUrl || '';
    if (!rawApiUrl) {
      this.popupTable = {
        ...this.popupTable,
        loading: false,
        errorMessage: popupColumns.length === 0 ? 'No popup columns were configured.' : '',
      };
      this.cdr.markForCheck();
      return;
    }

    const apiUrl = String(this.resolveDynamicValue(rawApiUrl, { row, column }) || '');
    const method = this.resolveMethod(apiUrl, popupConfig.method);
    const responsePath = String(this.resolveDynamicValue(popupConfig.responsePath, { row, column }) || '');
    const queryParams = this.resolveDynamicValue(popupConfig.queryParams, { row, column });
    const requestPayload = this.resolvePopupRequestPayload(apiUrl, popupConfig, row, column);
    const filters = this.buildRequestFilters(requestPayload, queryParams);

    this.configService
      .getConfig(apiUrl, popupConfig, filters, method)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const source = responsePath ? this.getValueByPath(response, responsePath) : response;
          const extractedRows = this.extractRowsFromSource(source, popupConfig);
          this.resolvePopupRowsForDisplay(extractedRows, popupConfig)
            .pipe(takeUntil(this.destroy$))
            .subscribe((rows) => {
              this.popupTable = {
                ...this.popupTable,
                rows,
                loading: false,
                errorMessage:
                  popupColumns.length === 0
                    ? 'No popup columns were configured.'
                    : '',
              };
              this.cdr.markForCheck();
            });
        },
        error: () => {
          this.popupTable = {
            ...this.popupTable,
            loading: false,
            errorMessage: 'Unable to load popup table rows.',
          };
          this.cdr.markForCheck();
        },
      });
  }

  closePopup(): void {
    this.popupOpen = false;
    this.popupTable = this.createEmptyTableState('popup');
    this.popupContextRow = null;
    this.popupContextColumn = undefined;
    this.cdr.markForCheck();
  }

  submitTable(): void {
    const submitAction = this.widgetConfig?.submitAction;
    if (!submitAction?.apiUrl || this.submitInFlight) {
      this.debug('submitTable skipped', {
        hasApiUrl: !!submitAction?.apiUrl,
        submitInFlight: this.submitInFlight,
      });
      return;
    }

    const apiUrl = String(this.resolveDynamicValue(submitAction.apiUrl, {}) || '');
    const method = this.resolveMethod(apiUrl, submitAction.method);
    const requestPayload = this.resolveDynamicValue(submitAction.requestPayload, {});
    const queryParams = this.resolveDynamicValue(submitAction.queryParams, {});
    const filters = this.buildRequestFilters(
      requestPayload ?? {
        editedCellCount: this.editedCellCount,
        rows: this.cloneObject(this.rows),
      },
      queryParams,
    );

    this.debug('submitTable request', {
      widgetId: this.widgetConfig?.widgetId,
      apiUrl,
      method,
      filters: this.summarizeFilters(filters),
      requestPayload,
    });

    this.submitInFlight = true;
    this.submitMessage = '';
    this.submitErrorMessage = '';
    this.cdr.markForCheck();

    this.configService
      .getConfig(apiUrl, submitAction, filters, method)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.debug('submitTable success', {
            widgetId: this.widgetConfig?.widgetId,
            responseKeys: Object.keys(response || {}),
          });
          const responseMessagePath = String(
            this.resolveDynamicValue(submitAction.responseMessagePath, {}) || '',
          );
          const messageFromResponse = responseMessagePath ? this.getValueByPath(response, responseMessagePath) : '';
          this.submitInFlight = false;
          this.submitMessage = String(messageFromResponse || submitAction.successMessage || 'Submitted successfully.');
          this.submitErrorMessage = '';
          if (submitAction.navigateAction) {
            executeNavigationFromConfig(
              this.router,
              { navigateAction: submitAction.navigateAction, widgetId: this.widgetConfig?.widgetId },
              { name: 'submit', value: 'submit' },
              { requestPayload, filters },
            );
          }
          this.flushAutoSubmit();
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.debug('submitTable error', {
            widgetId: this.widgetConfig?.widgetId,
            error,
          });
          this.submitInFlight = false;
          this.submitMessage = '';
          this.submitErrorMessage = submitAction.errorMessage || 'Submit failed.';
          this.flushAutoSubmit();
          this.cdr.markForCheck();
        },
      });
  }

  getSliderMin(row: any, column: SmartTableColumn): number {
    return this.resolveNumericSetting(row, column.minKey, column.min, 0);
  }

  getSliderMax(row: any, column: SmartTableColumn): number {
    return this.resolveNumericSetting(row, column.maxKey, column.max, 100);
  }

  getSliderStep(row: any, column: SmartTableColumn): number {
    return this.resolveNumericSetting(row, column.stepKey, column.step, this.getDefaultSliderStep(column));
  }

  getProgressPercent(row: any, column: SmartTableColumn): number {
    const value = Number(this.resolveProgressValue(row, column));
    const max = Number(this.resolveProgressMax(row, column));
    if (!Number.isFinite(value) || !Number.isFinite(max) || max <= 0) {
      return 0;
    }
    return Math.max(0, Math.min(100, (value / max) * 100));
  }

  getProgressLabel(row: any, column: SmartTableColumn): string {
    const value = Number(this.resolveProgressValue(row, column));
    if (!Number.isFinite(value)) {
      return '0%';
    }
    return `${this.formatNumericValue(value, { ...column, type: 'number' }, undefined)}`;
  }

  getProgressColor(row: any, column: SmartTableColumn): string {
    const colorByKey = column.progressColorKey ? this.getValueByPath(row, column.progressColorKey) : '';
    return String(colorByKey || column.progressColor || '');
  }

  getStatusColor(row: any, column: SmartTableColumn): string {
    const keyColor = column.statusColorKey ? this.getValueByPath(row, column.statusColorKey) : '';
    return String(keyColor || column.statusColor || '');
  }

  getStatusLabel(row: any, column: SmartTableColumn): string {
    const value = this.getValueByPath(row, column.key);
    return value === undefined || value === null || value === '' ? 'Pending' : String(value);
  }

  private runRecalculationRequest() {
    const recalculation = this.widgetConfig?.recalculation;
    const apiUrl = recalculation?.apiUrl;
    if (!apiUrl || !this.widgetConfig) {
      return of<RecalculationRules | null>(null);
    }

    const method = this.resolveMethod(apiUrl, recalculation.method);

    this.apiSyncing = true;
    this.syncMessage = 'Syncing derived columns...';
    this.updateSyncStateColumns('Syncing...', this.getConfiguredSyncStateColor('pending'));
    this.cdr.markForCheck();

    const requestPayload = {
      editedCellCount: this.editedCellCount,
      rows: this.cloneObject(this.rows),
    };

    return this.configService
      .getConfig(apiUrl, recalculation, { requestParams: requestPayload }, method)
      .pipe(
        catchError(() => {
          this.apiSyncing = false;
          this.syncMessage = 'Sync failed. JSON rule set could not be loaded.';
          this.updateSyncStateColumns('Sync failed', this.getConfiguredSyncStateColor('error'));
          this.cdr.markForCheck();
          return of<RecalculationRules | null>(null);
        }),
      );
  }

  private shouldRunRecalculation(): boolean {
    const recalculation = this.widgetConfig?.recalculation;
    return !!recalculation?.apiUrl && recalculation.enabled !== false && this.editedCellCount >= this.editTriggerCount;
  }

  private applyRecalculationRules(response: any): void {
    const recalculation = this.widgetConfig?.recalculation;
    if (!recalculation) {
      return;
    }

    const rules = this.extractRecalculationRules(response, recalculation.responsePath);
    const weights = rules.weights || {};
    const formula = this.resolveFormulaKeys(recalculation);
    const outputs = this.resolveOutputKeys(recalculation);
    const scoreKey = outputs.score || 'healthScore';
    const scoreColorKey = outputs.scoreColor || 'healthColor';
    const projectionKey = outputs.projection || 'projectedRevenue';
    const statusKey = outputs.status || 'autoStatus';
    const statusColorKey = outputs.statusColor || 'autoStatusColor';
    const syncMessage = rules.syncMessage || `Synced after ${this.editedCellCount} edits`;

    this.rows = this.rows.map((row) => {
      const confidence = this.toFiniteNumber(row[formula.confidenceKey || 'confidence']);
      const priceLift = this.toFiniteNumber(row[formula.priceLiftKey || 'priceLift']);
      const risk = this.toFiniteNumber(row[formula.riskKey || 'risk']);
      const resourceLoad = this.toFiniteNumber(row[formula.resourceLoadKey || 'resourceLoad']);
      const baseValue = this.toFiniteNumber(row[formula.baseValueKey || 'baseRevenue']);

      const score =
        25
        + (confidence * this.toFiniteNumber(weights['confidence']))
        + (priceLift * this.toFiniteNumber(weights['priceLift']))
        - (risk * this.toFiniteNumber(weights['riskPenalty']))
        - (resourceLoad * this.toFiniteNumber(weights['resourceLoadPenalty']))
        + (this.editedCellCount * this.toFiniteNumber(weights['editedCellBonus']));

      const boundedScore = Math.max(0, Math.min(100, Math.round(score)));
      const statusBand = this.resolveStatusBand(boundedScore, rules.statusBands || []);
      const projectedRevenue =
        (baseValue * (1 + priceLift / 100))
        + (confidence * this.toFiniteNumber(rules.projectionConfidenceBonus))
        - (risk * this.toFiniteNumber(rules.projectionRiskPenalty))
        - (resourceLoad * this.toFiniteNumber(rules.projectionLoadPenalty));

      return {
        ...row,
        [scoreKey]: boundedScore,
        [scoreColorKey]: statusBand.color,
        [projectionKey]: this.roundValue(projectedRevenue, 2),
        [statusKey]: statusBand.label,
        [statusColorKey]: statusBand.color,
      };
    });

    this.apiSyncing = false;
    this.syncMessage = syncMessage;
    this.updateSyncStateColumns(syncMessage, this.getConfiguredSyncStateColor('success'));
    this.cdr.markForCheck();
  }

  private updateSyncStateColumns(message: string, color: string): void {
    const outputs = this.resolveOutputKeys(this.widgetConfig?.recalculation);
    const syncKey = outputs.syncState || 'syncState';
    const syncColorKey = outputs.syncColor || 'syncColor';
    this.rows = this.rows.map((row) => ({
      ...row,
      [syncKey]: message,
      ...(color ? { [syncColorKey]: color } : {}),
    }));
  }

  private extractRecalculationRules(response: any, responsePath?: string): RecalculationRules {
    const fromPath = responsePath ? this.getValueByPath(response, responsePath) : undefined;
    const candidate = fromPath ?? response;
    return candidate && typeof candidate === 'object' ? candidate as RecalculationRules : {};
  }

  private resolveStatusBand(score: number, bands: RecalculationBand[]): { label: string; color: string } {
    const sortedBands = [...bands].sort((left, right) => this.toFiniteNumber(right.min) - this.toFiniteNumber(left.min));
    const band = sortedBands.find((item) => score >= this.toFiniteNumber(item.min));
    return {
      label: band?.label || 'Pending',
      color: band?.color || '',
    };
  }

  private getConfiguredSyncStateColor(state: 'pending' | 'success' | 'error'): string {
    return String(this.widgetConfig?.recalculation?.syncStateColors?.[state] || '');
  }

  private registerEdit(scope: SmartTableScope, rowIndex: number, row: any, column: SmartTableColumn): void {
    if (scope !== 'main') {
      return;
    }

    const shouldTrackRecalculation = this.shouldTrackColumnForRecalculation(column);
    this.lastEditedRow = this.cloneObject(row);
    this.debug('registerEdit', {
      widgetId: this.widgetConfig?.widgetId,
      rowIndex,
      column: column.bindingKey || column.key,
      value: row?.[column.key],
      shouldTrackRecalculation,
      shouldAutoSubmit: this.shouldAutoSubmitColumn(column),
      editedRow: this.lastEditedRow,
    });

    if (shouldTrackRecalculation) {
      const trackedKey = column.bindingKey || column.key;
      const editKey = `${this.getRowId(row, rowIndex)}::${trackedKey}`;
      this.editedCellKeys.add(editKey);
      this.syncMessage = this.getPendingSyncMessage();
      this.recalculationTrigger$.next();
    }

    if (this.shouldAutoSubmitColumn(column)) {
      this.scheduleAutoSubmit();
    }

    this.cdr.markForCheck();
  }

  private shouldAutoSubmitColumn(column: SmartTableColumn): boolean {
    const submitAction = this.widgetConfig?.submitAction;
    if (!submitAction?.apiUrl || submitAction.autoSubmitOnEdit !== true) {
      this.debug('shouldAutoSubmitColumn blocked by submitAction', {
        widgetId: this.widgetConfig?.widgetId,
        column: column.bindingKey || column.key,
        hasSubmitAction: !!submitAction,
        apiUrl: submitAction?.apiUrl,
        autoSubmitOnEdit: submitAction?.autoSubmitOnEdit,
      });
      return false;
    }

    const configuredColumns = Array.isArray(submitAction.triggerColumns)
      ? submitAction.triggerColumns.map((value) => String(value || '').trim()).filter((value) => !!value)
      : [];

    if (configuredColumns.length === 0) {
      this.debug('shouldAutoSubmitColumn default-mode', {
        widgetId: this.widgetConfig?.widgetId,
        column: column.bindingKey || column.key,
        isEditable: this.isEditableColumn(column),
        isDropdown: this.isDropdownColumn(column),
      });
      return this.isEditableColumn(column) || this.isDropdownColumn(column);
    }

    const candidates = [column.bindingKey, column.key]
      .map((value) => String(value || '').trim())
      .filter((value) => !!value);
    const matched = candidates.some((candidate) => configuredColumns.includes(candidate));
    this.debug('shouldAutoSubmitColumn trigger-match', {
      widgetId: this.widgetConfig?.widgetId,
      column: column.bindingKey || column.key,
      candidates,
      configuredColumns,
      matched,
    });
    return matched;
  }

  private scheduleAutoSubmit(): void {
    this.pendingAutoSubmit = true;
    this.debug('scheduleAutoSubmit', {
      widgetId: this.widgetConfig?.widgetId,
      delayMs: this.getAutoSubmitDelayMs(),
      editedRow: this.lastEditedRow,
    });

    if (this.autoSubmitTimer) {
      clearTimeout(this.autoSubmitTimer);
    }

    this.autoSubmitTimer = setTimeout(() => {
      this.autoSubmitTimer = undefined;
      this.flushAutoSubmit();
    }, this.getAutoSubmitDelayMs());
  }

  private flushAutoSubmit(): void {
    this.debug('flushAutoSubmit start', {
      widgetId: this.widgetConfig?.widgetId,
      pendingAutoSubmit: this.pendingAutoSubmit,
      submitInFlight: this.submitInFlight,
      editedRow: this.lastEditedRow,
    });
    if (!this.pendingAutoSubmit || this.submitInFlight) {
      return;
    }

    this.pendingAutoSubmit = false;
    this.syncEditedRowToGlobalFilters();
    this.submitTable();
  }

  private getAutoSubmitDelayMs(): number {
    const raw = Number(this.widgetConfig?.submitAction?.debounceMs);
    return Number.isFinite(raw) && raw >= 0 ? Math.floor(raw) : 500;
  }

  private syncEditedRowToGlobalFilters(): void {
    const submitAction = this.widgetConfig?.submitAction;
    const editedRow = this.lastEditedRow;
    if (!submitAction?.syncFiltersOnEdit || !editedRow) {
      this.debug('syncEditedRowToGlobalFilters skipped', {
        widgetId: this.widgetConfig?.widgetId,
        syncFiltersOnEdit: submitAction?.syncFiltersOnEdit,
        hasEditedRow: !!editedRow,
      });
      return;
    }

    const filterKeys = Array.isArray(submitAction.filterKeys) && submitAction.filterKeys.length > 0
      ? submitAction.filterKeys
      : (Array.isArray(submitAction.triggerColumns) ? submitAction.triggerColumns : []);
    if (filterKeys.length === 0) {
      return;
    }

    const listener = submitAction.filterListener || this.widgetConfig?.filterListener || this.widgetConfig?.filterListner;
    const normalizedKeys = filterKeys
      .map((key) => String(key || '').trim())
      .filter((key) => !!key && this.getValueByPath(editedRow, key) !== undefined);

    this.debug('syncEditedRowToGlobalFilters emit', {
      widgetId: this.widgetConfig?.widgetId,
      listener,
      normalizedKeys,
      values: normalizedKeys.reduce((acc: Record<string, any>, key) => {
        acc[key] = this.getValueByPath(editedRow, key);
        return acc;
      }, {}),
    });

    normalizedKeys.forEach((key, index) => {
      const isLast = index === normalizedKeys.length - 1;
      this.globalFilterService.updateFilterSelection(
        key,
        this.getValueByPath(editedRow, key),
        false,
        this.widgetConfig?.widgetId,
        isLast,
        listener,
        undefined,
        true,
      );
    });
  }

  private shouldTrackColumnForRecalculation(column: SmartTableColumn): boolean {
    const trackedColumns = this.getTriggerColumnKeys();
    if (trackedColumns.length === 0) {
      return this.isEditableColumn(column) || this.isDropdownColumn(column);
    }

    const candidates = [column.bindingKey, column.key]
      .map((value) => String(value || '').trim())
      .filter((value) => !!value);

    return candidates.some((candidate) => trackedColumns.includes(candidate));
  }

  private getTriggerColumnKeys(): string[] {
    const recalculation = this.widgetConfig?.recalculation;
    const triggerConfig = recalculation?.triggerAfterChanges;
    if (triggerConfig) {
      if (typeof triggerConfig === 'string') {
        return [triggerConfig.trim()].filter((value) => !!value);
      }

      if (Array.isArray(triggerConfig)) {
        return triggerConfig.map((value) => String(value || '').trim()).filter((value) => !!value);
      }

      if (typeof triggerConfig === 'object') {
        const raw =
          triggerConfig.columnKeys ||
          triggerConfig.keys ||
          triggerConfig.columns ||
          triggerConfig.bindingKeys ||
          [];

        return Array.isArray(raw)
          ? raw.map((value) => String(value || '').trim()).filter((value) => !!value)
          : [];
      }
    }

    return Array.isArray(recalculation?.watchColumns)
      ? recalculation!.watchColumns!.map((value) => String(value || '').trim()).filter((value) => !!value)
      : [];
  }

  private getPendingSyncMessage(): string {
    if (this.editedCellCount >= this.editTriggerCount) {
      return this.apiSyncing ? 'Syncing derived columns...' : 'Edit threshold met. Derived columns will sync automatically.';
    }

    const remaining = this.editTriggerCount - this.editedCellCount;
    return `${remaining} more editable ${remaining === 1 ? 'change' : 'changes'} needed before recalculation.`;
  }

  private normalizeColumns(config: Pick<SmartTableConfig | SmartTablePopupConfig, 'columns' | 'columnBindings'>): SmartTableColumn[] {
    const inputColumns = Array.isArray(this.inputData?.columns) && config === this.widgetConfig ? this.inputData!.columns! : [];
    const configColumns = Array.isArray(config.columns) ? config.columns : [];
    const sourceColumns = inputColumns.length > 0 ? inputColumns : configColumns;
    if (sourceColumns.length === 0) {
      return [];
    }

    const bindings = config.columnBindings || {};
    return sourceColumns.map((column) => this.resolveColumnBinding(column, bindings));
  }

  private resolveColumnBinding(
    column: SmartTableColumn,
    bindings: Record<string, Partial<SmartTableColumn>>,
  ): SmartTableColumn {
    const bindingKey = column.bindingKey || column.key;
    const binding = bindingKey ? bindings[bindingKey] : undefined;
    if (!binding) {
      return column;
    }

    const merged = {
      ...binding,
      ...column,
      key: column.key || binding.key || '',
      bindingKey,
    } as SmartTableColumn;

    if (!merged.key) {
      merged.key = bindingKey;
    }

    return merged;
  }

  private extractRowsFromSource(source: any, config: Pick<SmartTableConfig | SmartTablePopupConfig, 'rowsPath' | 'dataKey'>): any[] {
    if (Array.isArray(source)) {
      return this.cloneObject(source);
    }

    const candidatePaths = [config.rowsPath, config.dataKey].filter(Boolean) as string[];
    for (const path of candidatePaths) {
      const value = this.getValueByPath(source, path);
      if (Array.isArray(value)) {
        return this.cloneObject(value);
      }
    }

    const directRows = source?.tableRows || source?.rows || source?.data || source?.tableData;
    if (Array.isArray(directRows)) {
      return this.cloneObject(directRows);
    }

    return [];
  }

  private resolvePopupRowsForDisplay(rows: any[], popupConfig: SmartTablePopupConfig): Observable<any[]> {
    if (!popupConfig?.inheritApaPricingLever || !Array.isArray(rows) || rows.length === 0) {
      return of(rows);
    }

    const apaRangesUrl = String(
      popupConfig.apaRangesUrl || 'assets/mock-data/dvm/account-classification/apa-ranges.json',
    ).trim();

    if (!apaRangesUrl) {
      return of(rows);
    }

    return this.configService.getData(apaRangesUrl).pipe(
      map((response) => this.applyApaRangesToPopupRows(rows, response, popupConfig)),
      catchError(() => of(rows)),
    );
  }

  private resolveInitialRowsForDisplay(rows: any[], config: SmartTableConfig): Observable<any[]> {
    if (!config?.inheritApaPricingLever || !Array.isArray(rows) || rows.length === 0) {
      return of(rows);
    }

    const apaRangesUrl = String(
      config.apaRangesUrl || 'assets/mock-data/dvm/account-classification/apa-ranges.json',
    ).trim();

    if (!apaRangesUrl) {
      return of(rows);
    }

    return this.configService.getData(apaRangesUrl).pipe(
      map((response) => this.applyApaRangesToInitialRows(rows, response, config)),
      catchError(() => of(rows)),
    );
  }

  private applyApaRangesToPopupRows(rows: any[], source: any, popupConfig: SmartTablePopupConfig): any[] {
    const ranges = this.extractApaRanges(source, popupConfig);
    if (!ranges.length) {
      return rows;
    }

    const overrides = this.readApaOverridesMap();
    const labelKey = popupConfig.apaRangeLabelKey || 'label';
    const valueKey = popupConfig.apaRangeValueKey || 'currentValue';
    const productTypeKey = popupConfig.apaRangeProductTypeKey || 'product_price_type';
    const targetColumnKey = popupConfig.apaRangeTargetColumnKey || 'pricing_lever';
    const minKey = popupConfig.apaRangeMinKey || `${targetColumnKey}_min`;
    const maxKey = popupConfig.apaRangeMaxKey || `${targetColumnKey}_max`;
    const stepKey = popupConfig.apaRangeStepKey || `${targetColumnKey}_step`;

    return rows.map((row) => {
      const normalizedProductType = this.normalizeApaLabel(this.getValueByPath(row, productTypeKey));
      if (!normalizedProductType) {
        return row;
      }

      const matchedRange = ranges.find((range) => this.normalizeApaLabel(range?.[labelKey]) === normalizedProductType);
      if (!matchedRange) {
        return row;
      }

      const overrideValue = overrides[this.normalizeApaLabel(matchedRange?.[labelKey])];
      const rawValue = overrideValue ?? matchedRange?.[valueKey] ?? matchedRange?.value;
      const min = Number(matchedRange?.min);
      const max = Number(matchedRange?.max);
      const nextValue = this.clampPopupSliderValue(rawValue, min, max);

      return {
        ...row,
        [targetColumnKey]: nextValue,
        [minKey]: Number.isFinite(min) ? min : row?.[minKey],
        [maxKey]: Number.isFinite(max) ? max : row?.[maxKey],
        [stepKey]: this.resolveApaSliderStep(min, max),
      };
    });
  }

  private applyApaRangesToInitialRows(rows: any[], source: any, config: SmartTableConfig): any[] {
    const ranges = this.extractApaRanges(source, {
      apaRangeValueKey: config.apaRangeValueKey,
    } as SmartTablePopupConfig);
    if (!ranges.length) {
      return rows;
    }

    const overrides = this.readApaOverridesMap();
    const labelKey = config.apaRangeLabelKey || 'label';
    const valueKey = config.apaRangeValueKey || 'currentValue';
    const productTypeKey = config.apaRangeProductTypeKey || 'product_price_type';
    const targetColumnKey = config.apaRangeTargetColumnKey || 'pricing_lever';
    const minKey = config.apaRangeMinKey || `${targetColumnKey}_min`;
    const maxKey = config.apaRangeMaxKey || `${targetColumnKey}_max`;
    const stepKey = config.apaRangeStepKey || `${targetColumnKey}_step`;
    const currentFilters = this.globalFilterService.getCurrentFilters();
    const incomingLeverRaw =
      currentFilters?.requestParams?.[targetColumnKey] ??
      currentFilters?.queryParams?.[targetColumnKey];
    const incomingLever = Array.isArray(incomingLeverRaw) ? incomingLeverRaw[0] : incomingLeverRaw;
    const hasIncomingLever = Number.isFinite(Number(incomingLever));

    return rows.map((row) => {
      const normalizedProductType = this.normalizeApaLabel(this.getValueByPath(row, productTypeKey));
      if (!normalizedProductType) {
        return row;
      }

      const matchedRange = ranges.find((range) => this.normalizeApaLabel(range?.[labelKey]) === normalizedProductType);
      if (!matchedRange) {
        return row;
      }

      const overrideValue = overrides[this.normalizeApaLabel(matchedRange?.[labelKey])];
      const rawValue = hasIncomingLever
        ? incomingLever
        : (overrideValue ?? matchedRange?.[valueKey] ?? matchedRange?.value);
      const min = Number(matchedRange?.min);
      const max = Number(matchedRange?.max);
      const nextValue = this.clampPopupSliderValue(rawValue, min, max);

      return {
        ...row,
        [targetColumnKey]: nextValue,
        [minKey]: Number.isFinite(min) ? min : row?.[minKey],
        [maxKey]: Number.isFinite(max) ? max : row?.[maxKey],
        [stepKey]: this.resolveApaSliderStep(min, max),
      };
    });
  }

  private extractApaRanges(source: any, popupConfig: SmartTablePopupConfig): any[] {
    const direct = Array.isArray(source?.responseList?.[0]?.data)
      ? source.responseList[0].data
      : Array.isArray(source?.data)
        ? source.data
        : Array.isArray(source)
          ? source
          : [];

    if (!Array.isArray(direct)) {
      return [];
    }

    const valueKey = popupConfig.apaRangeValueKey || 'currentValue';
    const overrides = this.readApaOverridesMap();

    return direct.map((row) => {
      const label = row?.label;
      const overrideValue = overrides[this.normalizeApaLabel(label)];
      const fallbackValue = row?.currentValue ?? row?.value;
      return {
        ...row,
        [valueKey]: overrideValue ?? fallbackValue,
      };
    });
  }

  private readApaOverridesMap(): Record<string, number> {
    try {
      const raw = sessionStorage.getItem(JsonDrivenSmartTableComponent.APA_OVERRIDES_STORAGE_KEY);
      if (!raw) {
        return {};
      }
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  private normalizeApaLabel(value: any): string {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  private clampPopupSliderValue(value: any, min: number, max: number): number {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return Number.isFinite(min) ? min : 0;
    }

    if (Number.isFinite(min) && numericValue < min) {
      return min;
    }

    if (Number.isFinite(max) && numericValue > max) {
      return max;
    }

    return numericValue;
  }

  private resolveApaSliderStep(min: number, max: number): number {
    const range = max - min;
    if (!Number.isFinite(range) || range <= 0) {
      return 0.01;
    }

    const suggested = range / 100;
    return suggested >= 0.01 ? Number(suggested.toFixed(2)) : 0.01;
  }

  private mergeWidgetConfigs(remoteConfig: any, inputConfig: SmartTableConfig): SmartTableConfig {
    const remote = remoteConfig && typeof remoteConfig === 'object' ? remoteConfig as SmartTableConfig : {};
    return {
      ...remote,
      ...inputConfig,
      columns: Array.isArray(inputConfig.columns) && inputConfig.columns.length > 0 ? inputConfig.columns : remote.columns,
      columnBindings: this.mergePlainObjects(remote.columnBindings, inputConfig.columnBindings),
      rows: Array.isArray(inputConfig.rows) && inputConfig.rows.length > 0 ? inputConfig.rows : remote.rows,
      headerValues: this.mergePlainObjects(remote.headerValues, inputConfig.headerValues),
      rowPopup: this.mergePlainObjects(remote.rowPopup, inputConfig.rowPopup),
      recalculation: this.mergePlainObjects(remote.recalculation, inputConfig.recalculation),
      submitAction: this.mergePlainObjects(remote.submitAction, inputConfig.submitAction),
      themeOverrides: this.mergePlainObjects(remote.themeOverrides, inputConfig.themeOverrides),
    };
  }

  private mergePlainObjects<T>(base: T | undefined, override: T | undefined): T | undefined {
    if (!base) {
      return override;
    }
    if (!override) {
      return base;
    }

    const merged = { ...(base as Record<string, any>) };
    Object.keys(override as Record<string, any>).forEach((key) => {
      const overrideValue = (override as Record<string, any>)[key];
      const baseValue = (base as Record<string, any>)[key];
      if (
        overrideValue &&
        typeof overrideValue === 'object' &&
        !Array.isArray(overrideValue) &&
        baseValue &&
        typeof baseValue === 'object' &&
        !Array.isArray(baseValue)
      ) {
        merged[key] = this.mergePlainObjects(baseValue, overrideValue);
      } else {
        merged[key] = overrideValue;
      }
    });
    return merged as T;
  }

  private applyResolvedTheme(): void {
    const overrides = this.widgetConfig?.themeOverrides || this.inputData?.themeOverrides;
    const resolvedTheme = this.themeService.resolve(overrides);
    this.themeService.applyThemeTokensTo(resolvedTheme, this.hostElement.nativeElement);
  }

  private resolveMethod(url: string, explicitMethod?: string): string {
    if (explicitMethod) {
      return explicitMethod;
    }
    return String(url).endsWith('.json') ? 'get' : 'post';
  }

  private resolveNumericSetting(row: any, key: string | undefined, fallback: number | undefined, defaultValue: number): number {
    const fromRow = key ? Number(this.getValueByPath(row, key)) : NaN;
    if (Number.isFinite(fromRow)) {
      return fromRow;
    }

    const fromConfig = Number(fallback);
    if (Number.isFinite(fromConfig)) {
      return fromConfig;
    }

    return defaultValue;
  }

  private getDefaultSliderStep(column: SmartTableColumn): number {
    const decimals = this.getColumnDecimals(column);
    if (decimals <= 0) {
      return 1;
    }

    return 1 / (10 ** decimals);
  }

  private resolveProgressValue(row: any, column: SmartTableColumn): number {
    const key = column.progressValueKey || column.key;
    return this.toFiniteNumber(this.getValueByPath(row, key));
  }

  private resolveProgressMax(row: any, column: SmartTableColumn): number {
    if (column.progressMaxKey) {
      const maxFromRow = Number(this.getValueByPath(row, column.progressMaxKey));
      if (Number.isFinite(maxFromRow) && maxFromRow > 0) {
        return maxFromRow;
      }
    }

    const maxFromConfig = Number(column.progressMax);
    return Number.isFinite(maxFromConfig) && maxFromConfig > 0 ? maxFromConfig : 100;
  }

  isNumberLikeColumn(column: SmartTableColumn): boolean {
    return column.type === 'number' || column.type === 'currency' || column.type === 'slider' || column.type === 'progress';
  }

  private getRowId(row: any, rowIndex: number): string {
    const explicitId = row?.id ?? row?._id ?? row?.rowId;
    return explicitId !== undefined && explicitId !== null ? String(explicitId) : `row-${rowIndex}`;
  }

  private resolveColumnPixelWidth(width: string | undefined): number {
    const raw = String(width || '').trim().toLowerCase();
    if (!raw) {
      return 0;
    }

    if (/^\d+(\.\d+)?px$/.test(raw)) {
      return Number.parseFloat(raw);
    }

    if (/^\d+(\.\d+)?rem$/.test(raw)) {
      return Number.parseFloat(raw) * 16;
    }

    if (/^\d+(\.\d+)?$/.test(raw)) {
      return Number.parseFloat(raw);
    }

    return 0;
  }

  private formatCurrencyValue(value: any, column: SmartTableColumn, row?: any): string {
    const compact = this.formatCompactNumber(value, column, row);
    const rawPrefix = String(column.prefix || '$');
    const prefix = rawPrefix ? (rawPrefix.endsWith(' ') ? rawPrefix : `${rawPrefix} `) : '';
    const suffix = String(column.suffix || '');
    if (compact) {
      return `${prefix}${compact}${suffix}`;
    }

    const decimals = this.getColumnDecimals(column, 2);
    const numericValue = this.toFiniteNumber(value);
    return `${prefix}${this.formatNumberWithCommas(numericValue, decimals)}${suffix}`;
  }

  private formatNumericValue(value: any, column: SmartTableColumn, row?: any): string {
    const compact = this.formatCompactNumber(value, column, row);
    const rawPrefix = String(column.prefix || '');
    const prefix = rawPrefix ? (rawPrefix.endsWith(' ') ? rawPrefix : `${rawPrefix} `) : '';
    const suffix = String(column.suffix || '');
    if (compact) {
      return `${prefix}${compact}${suffix}`;
    }

    const numericValue = this.toFiniteNumber(value);
    const decimals = this.getColumnDecimals(column, 0);
    return `${prefix}${this.formatNumberWithCommas(numericValue, decimals)}${suffix}`;
  }

  private formatNumberWithCommas(value: number, decimals: number): string {
    if (decimals <= 0) {
      return commaSeparatedValue(value);
    }

    if (decimals === 1) {
      return oneDecimalWithCommas(value);
    }

    return value.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }

  private formatCompactNumber(value: any, column: SmartTableColumn, row?: any): string | null {
    const numericValue = this.toFiniteNumber(value);
    const unit = this.resolveCompactUnit(column, numericValue, row);
    if (!unit) {
      return null;
    }

    const divisor = unit === 'm' ? 1_000_000 : 1_000;
    const scaled = numericValue / divisor;
    const configuredDecimals = Number(column.compactDecimals);
    const decimals = Number.isFinite(configuredDecimals) && configuredDecimals >= 0
      ? Math.floor(configuredDecimals)
      : 1;

    return `${this.trimTrailingZeros(scaled.toFixed(decimals))}${unit.toUpperCase()}`;
  }

  private resolveCompactUnit(column: SmartTableColumn, numericValue: number, row?: any): 'k' | 'm' | null {
    if (!this.shouldUseCompactFormat(column, row)) {
      return null;
    }

    const fromRow = column.compactUnitKey
      ? this.normalizeCompactUnit(this.getValueByPath(row, column.compactUnitKey))
      : null;
    const configuredUnit = this.normalizeCompactUnit(column.compactUnit);
    const unit = fromRow || configuredUnit || 'auto';

    if (unit === 'k' || unit === 'm') {
      return unit;
    }

    const absolute = Math.abs(numericValue);
    if (absolute >= 1_000_000) {
      return 'm';
    }

    if (absolute >= 1_000) {
      return 'k';
    }

    return null;
  }

  private shouldUseCompactFormat(column: SmartTableColumn, row?: any): boolean {
    const fromRow = column.compactFormatKey
      ? this.toBooleanFlag(this.getValueByPath(row, column.compactFormatKey))
      : null;
    if (fromRow !== null) {
      return fromRow;
    }

    if (typeof column.compactFormat === 'boolean') {
      return column.compactFormat;
    }

    return !!column.compactUnit || !!column.compactUnitKey;
  }

  private normalizeCompactUnit(value: unknown): 'auto' | 'k' | 'm' | null {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'k' || normalized === 'm' || normalized === 'auto') {
      return normalized;
    }

    return null;
  }

  private toBooleanFlag(value: unknown): boolean | null {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'number') {
      if (value === 1) return true;
      if (value === 0) return false;
      return null;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (['true', '1', 'yes', 'y', 'on'].includes(normalized)) {
        return true;
      }

      if (['false', '0', 'no', 'n', 'off'].includes(normalized)) {
        return false;
      }
    }

    return null;
  }

  private trimTrailingZeros(value: string): string {
    return value
      .replace(/(\.\d*?[1-9])0+$/, '$1')
      .replace(/\.0+$/, '');
  }

  private normalizeNumericColumnValue(value: number, column: SmartTableColumn): number {
    const decimals = this.getColumnDecimals(column, 0);
    if (!Number.isFinite(value)) {
      return 0;
    }

    if (decimals <= 0) {
      return Math.round(value);
    }

    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
  }

  private parseNumber(value: string): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private toFiniteNumber(value: any): number {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : 0;
    }

    // If value is a string, try to extract numeric part (handles "21%", "$123.45", etc.)
    if (typeof value === 'string') {
      const cleaned = value.replace(/[^\d.-]/g, '');
      const parsed = Number(cleaned);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private roundValue(value: number, decimals: number): number {
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
  }

  private getColumnDecimals(column: SmartTableColumn, fallback = 0): number {
    const raw = Number(column.decimals);
    if (!Number.isFinite(raw) || raw < 0) {
      return fallback;
    }

    return Math.floor(raw);
  }

  private normalizeCssSize(value: string | number): string {
    return typeof value === 'number' ? `${value}px` : String(value);
  }

  private getValueByPath(source: any, path: string): any {
    if (!path) {
      return source;
    }

    return String(path)
      .split('.')
      .reduce((value: any, part: string) => (value == null ? undefined : value[part]), source);
  }

  private humanize(value: string): string {
    return String(value)
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (segment) => segment.toUpperCase());
  }

  private cloneObject<T>(value: T): T {
    if (Array.isArray(value)) {
      return value.map((item) => this.cloneObject(item)) as T;
    }

    if (value && typeof value === 'object') {
      return Object.keys(value as Record<string, any>).reduce((acc: any, key: string) => {
        acc[key] = this.cloneObject((value as Record<string, any>)[key]);
        return acc;
      }, {}) as T;
    }

    return value;
  }

  private ensureDropdownOptions(rowIndex: number, row: any, column: SmartTableColumn): void {
    const staticOptions = this.extractStaticOptions(column);
    if (staticOptions.length > 0 || !column.dropdownApiUrl) {
      return;
    }

    const cacheKey = this.buildDropdownCacheKey(rowIndex, row, column);
    if (this.dropdownOptionsCache.has(cacheKey) || this.dropdownLoadingKeys.has(cacheKey)) {
      return;
    }

    const apiUrl = String(this.resolveDynamicValue(column.dropdownApiUrl, { row, column }) || '');
    if (!apiUrl) {
      return;
    }

    const method = this.resolveMethod(apiUrl, column.dropdownMethod);
    const requestPayload = this.resolveDynamicValue(column.dropdownRequestPayload, { row, column });
    const queryParams = this.resolveDynamicValue(column.dropdownQueryParams, { row, column });
    const filters = this.buildRequestFilters(requestPayload, queryParams);
    const responsePath = String(this.resolveDynamicValue(column.dropdownResponsePath, { row, column }) || '');

    this.dropdownLoadingKeys.add(cacheKey);

    this.configService
      .getConfig(apiUrl, column, filters, method)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const source = responsePath ? this.getValueByPath(response, responsePath) : response;
          this.dropdownOptionsCache.set(cacheKey, this.normalizeDropdownOptions(source, column));
          this.dropdownLoadingKeys.delete(cacheKey);
          this.cdr.markForCheck();
        },
        error: () => {
          this.dropdownLoadingKeys.delete(cacheKey);
          this.cdr.markForCheck();
        },
      });
  }

  private extractStaticOptions(column: SmartTableColumn): SmartTableOption[] {
    return this.normalizeDropdownOptions(column.options || [], column);
  }

  private normalizeDropdownOptions(source: any, column: SmartTableColumn): SmartTableOption[] {
    const candidate = Array.isArray(source)
      ? source
      : source?.options || source?.dropdownOptions || source?.items || source?.data || [];

    if (!Array.isArray(candidate)) {
      return [];
    }

    const labelKey = column.dropdownLabelKey || 'label';
    const valueKey = column.dropdownValueKey || 'value';
    const disabledKey = column.dropdownDisabledKey || 'disabled';

    return candidate.map((item) => {
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        const label = item[labelKey] ?? item[valueKey] ?? item['name'] ?? item['title'] ?? '';
        const value = item[valueKey] ?? item[labelKey] ?? label;
        return {
          label: String(label),
          value,
          disabled: Boolean(item[disabledKey]),
        };
      }

      return {
        label: String(item),
        value: item,
        disabled: false,
      };
    });
  }

  private buildDropdownCacheKey(rowIndex: number, row: any, column: SmartTableColumn): string {
    const rowKey = column.dropdownRequestPayload || column.dropdownQueryParams ? this.getRowId(row, rowIndex) : 'shared';
    return `${column.bindingKey || column.key}::${rowKey}`;
  }

  private resolveDropdownValue(rawValue: string, rowIndex: number, row: any, column: SmartTableColumn): any {
    const options = this.getDropdownOptions(rowIndex, row, column);
    const matched = options.find((option) => String(option.value) === rawValue);
    return matched ? matched.value : rawValue;
  }

  private buildRequestFilters(requestPayload: any, queryParams: any): any {
    const filters: any = {};

    if (requestPayload !== undefined && requestPayload !== null && requestPayload !== '') {
      filters.requestParams = requestPayload;
    }

    if (queryParams && typeof queryParams === 'object' && !Array.isArray(queryParams)) {
      filters.queryParams = queryParams;
    }

    return Object.keys(filters).length > 0 ? filters : undefined;
  }

  private resolvePopupRequestPayload(
    apiUrl: string,
    popupConfig: SmartTablePopupConfig,
    row: any,
    column?: SmartTableColumn,
  ): any {
    const configuredPayload = this.resolveDynamicValue(popupConfig.requestPayload, { row, column });
    const defaultPayload = this.getDefaultPopupRequestPayload(apiUrl, row, column);
    const mode = String(popupConfig.requestPayloadMode || '').trim().toLowerCase();

    if (mode === 'none') {
      return undefined;
    }

    if (mode === 'custom') {
      return configuredPayload;
    }

    if (mode === 'merge') {
      return this.mergePayloadValues(defaultPayload, configuredPayload);
    }

    if (mode === 'auto') {
      return defaultPayload;
    }

    return configuredPayload ?? defaultPayload;
  }

  private getDefaultPopupRequestPayload(apiUrl: string, row: any, column?: SmartTableColumn): any {
    if (!apiUrl || this.isLocalAssetUrl(apiUrl)) {
      return undefined;
    }

    const clickedRow = this.cloneObject(row);
    const payload: Record<string, any> = {
      ...clickedRow,
      clickedRow,
      clickedRowId: this.getRowId(row, 0),
    };

    if (column?.key) {
      payload['clickedColumnKey'] = column.key;
      payload['clickedColumnValue'] = this.getValueByPath(row, column.key);
      payload[column.key] = this.getValueByPath(row, column.key);
    }

    return payload;
  }

  private mergePayloadValues(base: any, override: any): any {
    if (base == null) {
      return override;
    }
    if (override == null) {
      return base;
    }

    if (
      base &&
      typeof base === 'object' &&
      !Array.isArray(base) &&
      override &&
      typeof override === 'object' &&
      !Array.isArray(override)
    ) {
      return {
        ...base,
        ...override,
      };
    }

    return override;
  }

  private resolvePopupHeaderActions(
    popupConfig: SmartTablePopupConfig,
    row: any,
    column: SmartTableColumn | undefined,
  ): SmartTablePopupAction[] {
    const actions = Array.isArray(popupConfig.headerActions) ? popupConfig.headerActions : [];
    return actions
      .map((action) => ({
        ...action,
        label: String(this.resolveDynamicValue(action.label || '', { row, column }) || ''),
        navigateAction: this.resolveDynamicValue(action.navigateAction, { row, column }),
      }))
      .filter((action) => !!action.label);
  }

  private resolveFormulaKeys(recalculation?: SmartTableRecalculationConfig): SmartTableFormulaConfig {
    const formula = recalculation?.formula || {};
    const inputKeys = recalculation?.inputKeys || {};

    return {
      confidenceKey: formula.confidenceKey || inputKeys.confidence,
      priceLiftKey: formula.priceLiftKey || inputKeys.priceLift,
      riskKey: formula.riskKey || inputKeys.risk,
      resourceLoadKey: formula.resourceLoadKey || inputKeys.resourceLoad,
      baseValueKey: formula.baseValueKey || inputKeys.baseRevenue,
    };
  }

  private resolveOutputKeys(recalculation?: SmartTableRecalculationConfig): {
    score?: string;
    scoreColor?: string;
    projection?: string;
    status?: string;
    statusColor?: string;
    syncState?: string;
    syncColor?: string;
  } {
    const outputKeys = recalculation?.outputKeys || {};
    return {
      score: outputKeys.score || recalculation?.scoreKey,
      scoreColor: outputKeys.scoreColor || recalculation?.scoreColorKey,
      projection: outputKeys.projection || recalculation?.projectionKey,
      status: outputKeys.status || recalculation?.statusKey,
      statusColor: outputKeys.statusColor || recalculation?.statusColorKey,
      syncState: outputKeys.syncState || recalculation?.syncKey,
      syncColor: outputKeys.syncColor || recalculation?.syncColorKey,
    };
  }

  private createEmptyTableState(scope: SmartTableScope): SmartTableViewState {
    return {
      scope,
      title: '',
      columns: [],
      rows: [],
      loading: false,
      errorMessage: '',
      tableHeight: scope === 'main' ? this.tableHeight : '22rem',
      emptyStateMessage: scope === 'main' ? this.emptyStateMessage : 'No popup rows available.',
      headerValues: undefined,
      headerActions: [],
    };
  }

  private loadRowsFromConfig(config: SmartTableConfig, filters?: any): void {
    const inlineRows = this.extractRowsFromSource(config.rows, config);
    if (inlineRows.length > 0) {
      this.debug('loadRowsFromConfig inline rows', {
        widgetId: config.widgetId,
        rowCount: inlineRows.length,
      });
      this.resolveInitialRowsForDisplay(inlineRows, config)
        .pipe(takeUntil(this.destroy$))
        .subscribe((rows) => {
          this.rows = rows;
          this.loading = false;
          this.errorMessage = '';
          this.syncMessage = this.getPendingSyncMessage();
          this.cdr.markForCheck();
        });
      return;
    }

    const rowsUrl = config.apiUrl || config.mockDataUrl;
    if (!rowsUrl) {
      this.debug('loadRowsFromConfig no url', {
        widgetId: config.widgetId,
      });
      this.rows = [];
      this.loading = false;
      this.errorMessage = '';
      this.syncMessage = this.getPendingSyncMessage();
      this.cdr.markForCheck();
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.debug('loadRowsFromConfig request', {
      widgetId: config.widgetId,
      rowsUrl,
      method: this.resolveMethod(rowsUrl, config.method),
      filters: this.summarizeFilters(filters),
    });
    this.cdr.markForCheck();

    this.configService
      .getConfig(rowsUrl, config, filters, this.resolveMethod(rowsUrl, config.method))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.debug('loadRowsFromConfig success', {
            widgetId: config.widgetId,
            responseKeys: Object.keys(response || {}),
            extractedRowCount: this.extractRowsFromSource(response, config).length,
          });
          const extractedRows = this.extractRowsFromSource(response, config);
          this.resolveInitialRowsForDisplay(extractedRows, config)
            .pipe(takeUntil(this.destroy$))
            .subscribe((rows) => {
              this.rows = rows;
              this.loading = false;
              this.errorMessage = '';
              this.syncMessage = this.getPendingSyncMessage();
              this.cdr.markForCheck();
            });
        },
        error: (error) => {
          this.debug('loadRowsFromConfig error', {
            widgetId: config.widgetId,
            error,
          });
          this.loading = false;
          this.errorMessage = 'Unable to load table rows.';
          this.cdr.markForCheck();
        },
      });
  }

  private getInitialFiltersForWidget(config: SmartTableConfig): any | undefined {
    const navigationFilters = this.globalFilterService.getNavigationFiltersForWidget(config?.widgetId);
    if (navigationFilters) {
      this.debug('getInitialFiltersForWidget navigation', {
        widgetId: config?.widgetId,
        filters: this.summarizeFilters(navigationFilters),
      });
      return navigationFilters;
    }

    const filters = this.globalFilterService.getCurrentFilters();
    const hasFilters = !!(
      Object.keys(filters?.queryParams || {}).length ||
      Object.keys(filters?.requestParams || {}).length ||
      filters?.filterListener
    );

    if (!hasFilters || !config?.widgetId) {
      this.debug('getInitialFiltersForWidget empty', {
        widgetId: config?.widgetId,
        hasFilters,
      });
      return undefined;
    }

    const shouldReload = this.chartFilterHandler.shouldReload(filters, config.widgetId);
    this.debug('getInitialFiltersForWidget current', {
      widgetId: config.widgetId,
      shouldReload,
      filters: this.summarizeFilters(filters),
    });
    return shouldReload ? filters : undefined;
  }

  private debug(message: string, payload?: any): void {
    console.log(`${JsonDrivenSmartTableComponent.DEBUG_KEY} [SmartTable] ${message}`, payload);
  }

  private summarizeFilters(filters: any): any {
    if (!filters) {
      return filters;
    }

    return {
      widgetId: filters?.widgetId,
      filterListener: filters?.filterListener ?? filters?.filterListner,
      requestParams: filters?.requestParams,
      queryParams: filters?.queryParams,
      skipReload: filters?.skipReload,
      useQueryParams: filters?.useQueryParams,
    };
  }

  private resolveRowPopupConfig(scope: SmartTableScope, row: any, rowIndex: number): SmartTableRowPopupConfig | null {
    if (scope !== 'main') {
      return null;
    }

    const config = this.widgetConfig?.rowPopup;
    if (!config || config.enabled === false) {
      return null;
    }

    const rowIndexes = Array.isArray(config.rowIndexes) ? config.rowIndexes : [];
    if (this.matchesRowIndexSelector(rowIndexes, rowIndex)) {
      return config;
    }

    const rowIds = Array.isArray(config.rowIds) ? config.rowIds.map((value) => String(value)) : [];
    if (rowIds.length > 0) {
      const rowIdKey = config.rowIdKey || 'id';
      const candidateId = this.getValueByPath(row, rowIdKey);
      if (candidateId !== undefined && candidateId !== null && rowIds.includes(String(candidateId))) {
        return config;
      }
    }

    if (config.matchKey) {
      const rowValue = this.getValueByPath(row, config.matchKey);
      if (Array.isArray(config.matchValues) && config.matchValues.some((value) => String(value) === String(rowValue))) {
        return config;
      }
      if (config.matchValue !== undefined && String(config.matchValue) === String(rowValue)) {
        return config;
      }
    }

    return rowIndexes.length === 0 && rowIds.length === 0 && !config.matchKey ? config : null;
  }

  private matchesRowIndexSelector(indexes: Array<number | 'n' | 'N'>, rowIndex: number): boolean {
    if (indexes.length === 0) {
      return false;
    }

    const normalized = indexes
      .map((value) => this.normalizeRowIndexValue(value))
      .filter((value): value is number => Number.isFinite(value));

    if (normalized.includes(rowIndex)) {
      return true;
    }

    if (normalized.length === 2) {
      const [start, end] = normalized;
      const rangeStart = Math.min(start, end);
      const rangeEnd = Math.max(start, end);
      return rowIndex >= rangeStart && rowIndex <= rangeEnd;
    }

    return false;
  }

  private normalizeRowIndexValue(value: number | 'n' | 'N'): number {
    if (value === 'n' || value === 'N') {
      return Math.max(0, this.rows.length - 1);
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : NaN;
  }

  private isLocalAssetUrl(url: string): boolean {
    return url.startsWith('assets') || url.startsWith('/assets');
  }

  private isInteractiveTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) {
      return false;
    }

    return !!target.closest('button, input, select, textarea, a, label');
  }

  private resolveDynamicValue(value: any, context: SmartTableTemplateContext): any {
    if (Array.isArray(value)) {
      return value.map((item) => this.resolveDynamicValue(item, context));
    }

    if (value && typeof value === 'object') {
      return Object.keys(value).reduce((acc: Record<string, any>, key: string) => {
        acc[key] = this.resolveDynamicValue(value[key], context);
        return acc;
      }, {});
    }

    if (typeof value !== 'string') {
      return value;
    }

    if (value.startsWith('$row.')) {
      return this.getValueByPath(context.row, value.slice(5));
    }
    if (value === '$row') {
      return context.row;
    }
    if (value.startsWith('$column.')) {
      return this.getValueByPath(context.column, value.slice(8));
    }
    if (value === '$column') {
      return context.column;
    }
    if (value === '$rows') {
      return this.cloneObject(this.rows);
    }
    if (value === '$editedRow') {
      return this.cloneObject(this.lastEditedRow);
    }
    if (value === '$editedCellCount') {
      return this.editedCellCount;
    }

    const exactMatch = value.match(/^\{\{\s*([^}]+)\s*\}\}$/);
    if (exactMatch) {
      return this.resolveDynamicToken(exactMatch[1], context);
    }

    return value.replace(/\{\{\s*([^}]+)\s*\}\}/g, (_match, token: string) => {
      const resolved = this.resolveDynamicToken(token, context);
      return resolved === undefined || resolved === null ? '' : String(resolved);
    });
  }

  private resolveDynamicToken(token: string, context: SmartTableTemplateContext): any {
    const normalized = token.trim();

    if (normalized === 'row') {
      return context.row;
    }
    if (normalized.startsWith('row.')) {
      return this.getValueByPath(context.row, normalized.slice(4));
    }
    if (normalized === 'column') {
      return context.column;
    }
    if (normalized.startsWith('column.')) {
      return this.getValueByPath(context.column, normalized.slice(7));
    }
    if (normalized === 'rows') {
      return this.cloneObject(this.rows);
    }
    if (normalized === 'editedRow') {
      return this.cloneObject(this.lastEditedRow);
    }
    if (normalized.startsWith('editedRow.')) {
      return this.getValueByPath(this.lastEditedRow, normalized.slice(10));
    }
    if (normalized === 'editedCellCount') {
      return this.editedCellCount;
    }
    if (normalized === 'widget') {
      return this.widgetConfig;
    }
    if (normalized.startsWith('widget.')) {
      return this.getValueByPath(this.widgetConfig, normalized.slice(7));
    }
    if (normalized === 'event') {
      return context.event;
    }
    if (normalized.startsWith('event.')) {
      return this.getValueByPath(context.event, normalized.slice(6));
    }

    return '';
  }
}
